import type { CapturedRequest } from './api.js';

/**
 * How a captured body should be rendered.
 *
 * Driven by `content-type` first, because that is what the sender claimed and
 * what a debugging tool should show them. The one place we overrule the header
 * is when it says text but the bytes are not: a `content-type: application/json`
 * on a PNG has to render as hex, or the view fills with replacement characters.
 */
export type BodyKind = 'empty' | 'json' | 'form' | 'xml' | 'text' | 'binary';

export interface DecodedBody {
  kind: BodyKind;
  /** Text for every kind but `binary`; empty for `empty`. */
  text: string;
  /** Raw bytes. Only populated for `binary`, where hex is the only honest view. */
  bytes?: Uint8Array;
  /** Parsed value for `json`; `undefined` when the JSON did not parse. */
  json?: unknown;
  /** Decoded pairs for `form`. */
  pairs?: [string, string][];
  /** True when the JSON header lied and the payload does not parse. */
  malformed?: boolean;
}

function mediaType(contentType: string | null): string {
  return (contentType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
}

export function looksLikeJson(contentType: string | null): boolean {
  const type = mediaType(contentType);
  return type === 'application/json' || type.endsWith('+json') || type === 'text/json';
}

export function looksLikeXml(contentType: string | null): boolean {
  const type = mediaType(contentType);
  return type === 'application/xml' || type === 'text/xml' || type.endsWith('+xml');
}

export function looksLikeForm(contentType: string | null): boolean {
  return mediaType(contentType) === 'application/x-www-form-urlencoded';
}

export function looksLikeText(contentType: string | null): boolean {
  const type = mediaType(contentType);
  return (
    type.startsWith('text/') ||
    looksLikeJson(contentType) ||
    looksLikeXml(contentType) ||
    looksLikeForm(contentType) ||
    type === 'application/javascript' ||
    type === 'application/graphql'
  );
}

export function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/**
 * `application/x-www-form-urlencoded` as ordered pairs rather than an object:
 * a form can repeat a key (`tag=a&tag=b`), and collapsing that into an object
 * would silently drop half of what the sender actually posted.
 */
export function parseFormPairs(text: string): [string, string][] {
  return [...new URLSearchParams(text).entries()];
}

export function decodeBody(
  request: Pick<CapturedRequest, 'body' | 'bodyEncoding' | 'contentType'>,
): DecodedBody {
  if (request.body === null || request.body.length === 0) {
    return { kind: 'empty', text: '' };
  }

  if (request.bodyEncoding === 'base64') {
    const bytes = decodeBase64(request.body);

    // The capture side only reaches for base64 when the bytes are not storable
    // text, so this really is binary however the header describes it.
    return { kind: 'binary', text: '', bytes };
  }

  const text = request.body;

  if (looksLikeJson(request.contentType)) {
    try {
      return { kind: 'json', text, json: JSON.parse(text) };
    } catch {
      // Broken JSON is the single most common thing people come here to find.
      // Show the raw text and say it did not parse, rather than hiding either.
      return { kind: 'text', text, malformed: true };
    }
  }

  if (looksLikeForm(request.contentType)) {
    return { kind: 'form', text, pairs: parseFormPairs(text) };
  }

  if (looksLikeXml(request.contentType)) {
    return { kind: 'xml', text };
  }

  return { kind: 'text', text };
}

/** Bytes as `1,2 KB`. Sizes here are informative, not accounting. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
